import * as React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { fromUnixTime } from 'date-fns';

import { cn, formatDateToDayMonthYear } from '@restworld/utils-common';
import { Badge, Heading, IconButton } from '@restworld/ui-ds-base';

export interface FlexibleSlotCardProps {
  title: string;
  dateLabel: string;
  date?: string;
  cancelAt?: number;
  currentPeriodEnd?: number;
  status: 'active' | 'idle';
  orderNumber: number;
  onSlotCloseCallback?: () => void;
  showCrossIcon?: boolean;
  canceledAtPeriodEnd?: boolean;
  isExpirePeriodEnding?: boolean;
  isTrial?: boolean;
}

export const FlexibleSlotCard = ({ status, ...rest }: FlexibleSlotCardProps) =>
  status === 'active' ? (
    <ActiveFlexibleSlotCard status="active" {...rest} />
  ) : (
    <IdleFlexibleSlotCard status="idle" {...rest} />
  );

const SlotCardWrapper = ({
  children,
  orderNumber,
  title,
  isActive,
  status,
  isTrial,
}: {
  children: React.ReactNode;
  isActive?: boolean;
} & Pick<FlexibleSlotCardProps, 'orderNumber' | 'title' | 'status' | 'isTrial'>) => {
  return (
    <div
      className={cn(
        'w-full self-stretch p-4 rounded-lg inline-flex flex-col gap-2.5 md:flex-row md:justify-between md:items-center lg:h-[56px]',
        status === 'active' ? 'bg-purple-50' : 'border border-neutral-40'
      )}
    >
      <div className={cn('items-center gap-2 flex justify-start')}>
        <div
          className={cn(
            'px-2 py-0.5 rounded border justify-center items-center flex h-6',
            status === 'active' ? 'bg-purple-50 border-purple-700' : 'bg-neutral-40 border-neutral-200'
          )}
        >
          <Heading as="span" size={6} weight="medium" color={isActive ? 'primary-light' : 'neutral-light'}>
            {orderNumber}
          </Heading>
        </div>
        <Heading as="span" size={5} weight="medium" color={isActive ? 'neutral' : 'neutral-light'}>
          {title}
        </Heading>
        {isTrial && <Badge variant={isActive ? 'tertiary' : 'info'}>Prova 10 giorni</Badge>}
      </div>
      <div className="items-center gap-2 flex justify-between">{children}</div>
    </div>
  );
};

export const ActiveFlexibleSlotCard = ({
  dateLabel,
  date,
  cancelAt,
  currentPeriodEnd,
  onSlotCloseCallback,
  showCrossIcon = true,
  canceledAtPeriodEnd,
  isExpirePeriodEnding,
  ...rest
}: FlexibleSlotCardProps) => {
  const { t } = useTranslation('common');
  const showNextPaymentDate = !!cancelAt && !!currentPeriodEnd && cancelAt <= currentPeriodEnd ? false : true;
  return (
    <SlotCardWrapper isActive {...rest}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 w-full md:w-auto">
        <div className="flex items-center justify-between w-full md:w-auto">
          {showNextPaymentDate && (
            <div className="flex gap-1">
              <Heading
                as="span"
                size={6}
                weight="medium"
                color={isExpirePeriodEnding ? 'warning' : 'primary-light'}
                className="!text-sm"
              >
                {dateLabel}
              </Heading>
              <Heading as="span" size={6} weight="bold" color={isExpirePeriodEnding ? 'warning' : 'primary-light'}>
                {date}
              </Heading>
            </div>
          )}
          {showCrossIcon && (
            <IconButton
              ariaLabel="flexible-slot-cancel-icon"
              size="sm"
              Icon={XMarkIcon}
              className="block md:hidden"
              onClick={onSlotCloseCallback}
              variant="restworld"
            />
          )}
        </div>
        <Badge className="rounded max-w-fit lowercase" variant="tertiary">
          {canceledAtPeriodEnd && cancelAt
            ? `${t('canceled_at_period_end')} ${formatDateToDayMonthYear(fromUnixTime(cancelAt)?.toISOString())}`
            : t('active')}
        </Badge>
      </div>
      <IconButton
        ariaLabel="flexible-slot-cancel-icon"
        size="sm"
        Icon={XMarkIcon}
        className={showCrossIcon ? 'hidden md:grid' : 'hidden md:invisible md:grid'}
        onClick={onSlotCloseCallback}
        variant="restworld"
      />
    </SlotCardWrapper>
  );
};

export const IdleFlexibleSlotCard = ({ dateLabel, date, ...rest }: FlexibleSlotCardProps) => {
  return (
    <SlotCardWrapper {...rest}>
      <Badge variant="neutral">
        <div className="flex gap-1">
          <Heading as="span" size={6} weight="medium" color="neutral-light" className="!text-sm">
            {dateLabel}
          </Heading>
          <Heading as="span" size={6} weight="bold" color="neutral-light">
            {date}
          </Heading>
        </div>
      </Badge>
    </SlotCardWrapper>
  );
};

export default FlexibleSlotCard;
